import React, { ReactElement } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/core';

import FontAwesome from 'react-fontawesome';
import { OL_COLOUR_BLACK_55 } from '../Style/colours';
import { MIXIN_SCREEN_READER_TEXT } from '../Style/mixins';

const AUTO_SAVE_STATE_TEXT_NORMAL = 'Your change will be automatically saved';
const AUTO_SAVE_STATE_TEXT_SAVING = 'Your change is being saved';
const AUTO_SAVE_STATE_TEXT_SAVED = 'Your change was saved';

export enum AutoSaveState {
  Normal = 'normal',
  Saving = 'saving',
  Saved = 'saved',
}

const fadeOutKeyFrames = keyframes({
  from: {
    opacity: 1,
  },
  to: {
    opacity: 0,
  },
});

interface AutoSaveIconProps {
  state: AutoSaveState;
}

const AutoSaveIcon = styled('span')<AutoSaveIconProps>((props) => [
  {
    color: OL_COLOUR_BLACK_55,
    tranition: 'none',
    opacity: 1,

    '.fa': {
      fontWeight: 400,
    },
  },
  // show tick icon for one second and fade out
  props.state === AutoSaveState.Saved
    ? {
        animationDelay: '1s',
        animationName: fadeOutKeyFrames,
        animationTimingFunction: 'ease',
        animationDuration: '0.3s',
        animationFillMode: 'forwards',
      }
    : {},
]);

const AutoSaveScreenReaderText = styled.span(MIXIN_SCREEN_READER_TEXT);

interface SwatchInterface {
  state?: AutoSaveState;
}

const content = (state: AutoSaveState): ReactElement | null => {
  switch (state) {
    case AutoSaveState.Saving:
      return (
        <>
          <FontAwesome
            title={AUTO_SAVE_STATE_TEXT_SAVING}
            spin
            name="spinner"
          />
          <AutoSaveScreenReaderText>
            {AUTO_SAVE_STATE_TEXT_SAVING}
          </AutoSaveScreenReaderText>
        </>
      );
    case AutoSaveState.Saved:
      return (
        <>
          <FontAwesome title={AUTO_SAVE_STATE_TEXT_SAVED} name="check" />
          <AutoSaveScreenReaderText>
            {AUTO_SAVE_STATE_TEXT_SAVED}
          </AutoSaveScreenReaderText>
        </>
      );
    case AutoSaveState.Normal:
    default:
      return (
        <>
          <AutoSaveScreenReaderText>
            {AUTO_SAVE_STATE_TEXT_NORMAL}
          </AutoSaveScreenReaderText>
        </>
      );
  }
};

export const AutoSaveIndicator = ({
  state = AutoSaveState.Normal,
}: SwatchInterface): ReactElement => (
  <AutoSaveIcon state={state} role="status">
    {content(state)}
  </AutoSaveIcon>
);
